import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Tabs } from '@artosera/ui';
import {
  evenementsApi,
  useActionEvenement,
  useEvenement,
  type CorpsEvenement,
} from '../../api/evenements.js';
import { estApiError } from '../../api/errors.js';
import { enregistrerFichier } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { CompteursEvenement } from './EvenementsPage.js';
import { FormulaireEvenement } from './FormulaireEvenement.js';
import { InscritsEvenement } from './InscritsEvenement.js';
import { MessagesEvenement } from './MessagesEvenement.js';
import styles from './Evenements.module.css';

type Geste = 'publier' | 'annuler' | 'regenerer-lien';
export function EvenementDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const lire = galerieALaction(galerie, 'events:read');
  const fiche = useEvenement(galerieSlug, id, lire);
  const [onglet, setOnglet] = useState('informations');
  const [confirmation, setConfirmation] = useState<Geste | null>(null);
  const [avis, setAvis] = useState('');
  const [erreurFichier, setErreurFichier] = useState('');
  const [telecharge, setTelecharge] = useState(false);
  const modification = useActionEvenement(
    galerieSlug,
    (corps: CorpsEvenement & { version: number }, appel) =>
      evenementsApi.modifier(galerieSlug, id, corps, appel),
  );
  const geste = useActionEvenement(galerieSlug, (action: Geste, appel) =>
    evenementsApi.geste(galerieSlug, id, action, appel),
  );
  const evenement = fiche.data?.evenement;
  async function telecharger(fichier: 'qr.svg' | 'inscriptions.csv') {
    setErreurFichier('');
    setTelecharge(true);
    try {
      const resultat = await dansLaPorteeCourante((appel) =>
        evenementsApi.telecharger(galerieSlug, id, fichier, appel),
      );
      enregistrerFichier(resultat, `${evenement!.numero}-${fichier}`);
    } catch {
      setErreurFichier(t('commun:erreurReseau'));
    } finally {
      setTelecharge(false);
    }
  }
  if (!lire) return <p role="alert">{t('evenements.accesRefuse')}</p>;
  if (fiche.isError) return <p role="alert">{t('commun:erreurReseau')}</p>;
  if (!evenement) return <p role="status">{t('commun:chargement')}</p>;
  const ecrire = galerieALaction(galerie, 'events:write');
  const modifiable = ecrire && evenement.etat !== 'annule';
  return (
    <section className={styles.page}>
      <header>
        <Link to={`/${galerieSlug}/evenements`}>{t('evenements.titre')}</Link>
        <h1>
          {evenement.numero} ·{' '}
          {evenement.titre[i18n.language === 'en' ? 'en' : 'fr'] || evenement.titre.fr}
        </h1>
        <p>{t(`evenements.phases.${evenement.phase}`)}</p>
        <CompteursEvenement evenement={evenement} />
        <div className={styles.actions}>
          {modifiable && evenement.etat === 'brouillon' ? (
            <Button onClick={() => setConfirmation('publier')}>
              {t('evenements.gestesEvenement.publier')}
            </Button>
          ) : null}
          {modifiable ? (
            <Button variant="secondary" onClick={() => setConfirmation('annuler')}>
              {t('evenements.gestesEvenement.annuler')}
            </Button>
          ) : null}
          {galerieALaction(galerie, 'events:checkin') ? (
            <Link to={`/${galerieSlug}/evenements/${id}/accueil`}>{t('evenements.accueil')}</Link>
          ) : null}
        </div>
      </header>
      {avis ? <p role="status">{avis}</p> : null}
      {erreurFichier ? <p role="alert">{erreurFichier}</p> : null}
      <Tabs
        value={onglet}
        onValueChange={setOnglet}
        tabs={[
          {
            value: 'informations',
            label: t('evenements.informations'),
            content: (
              <>
                <FormulaireEvenement
                  key={`${id}-${evenement.version}`}
                  evenement={evenement}
                  lectureSeule={!modifiable}
                  occupe={modification.isPending}
                  onEnregistrer={(corps) => {
                    setAvis('');
                    modification.mutate(
                      { ...corps, version: evenement.version },
                      {
                        onSuccess: () => setAvis(t('evenements.enregistre')),
                        onError: (erreur) => {
                          if (estApiError(erreur) && erreur.status === 409) {
                            setAvis(t('evenements.conflit'));
                            void fiche.refetch();
                          }
                        },
                      },
                    );
                  }}
                />
                {modification.isError ? (
                  <p role="alert">
                    {estApiError(modification.error)
                      ? modification.error.message
                      : t('commun:erreurReseau')}
                  </p>
                ) : null}
              </>
            ),
          },
          {
            value: 'inscrits',
            label: t('evenements.inscrits'),
            content: (
              <>
                {ecrire ? (
                  <Button
                    disabled={telecharge}
                    onClick={() => void telecharger('inscriptions.csv')}
                  >
                    {t('evenements.exporter')}
                  </Button>
                ) : null}
                <InscritsEvenement evenement={evenement} />
              </>
            ),
          },
          {
            value: 'messages',
            label: t('evenements.messages'),
            content: <MessagesEvenement id={id} peutEcrire={modifiable} />,
          },
          {
            value: 'partager',
            label: t('evenements.partager'),
            content: (
              <div className={styles.formulaire}>
                <Field htmlFor="evt-lien" label={t('evenements.lienPublic')}>
                  <Input id="evt-lien" readOnly value={evenement.lienPublic} />
                </Field>
                <Button
                  onClick={() => {
                    void navigator.clipboard.writeText(evenement.lienPublic).then(
                      () => setAvis(t('evenements.copie')),
                      () => setErreurFichier(t('evenements.echecCopie')),
                    );
                  }}
                >
                  {t('evenements.copier')}
                </Button>
                <Button disabled={telecharge} onClick={() => void telecharger('qr.svg')}>
                  {t('evenements.telechargerQr')}
                </Button>
                {modifiable ? (
                  <Button variant="secondary" onClick={() => setConfirmation('regenerer-lien')}>
                    {t('evenements.gestesEvenement.regenerer-lien')}
                  </Button>
                ) : null}
              </div>
            ),
          },
        ]}
      />
      <Dialog
        open={confirmation !== null}
        onOpenChange={(v) => {
          if (!v) setConfirmation(null);
        }}
        title={confirmation ? t(`evenements.gestesEvenement.${confirmation}`) : ''}
        description={confirmation ? t(`evenements.confirmations.${confirmation}`) : ''}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <Button
            loading={geste.isPending}
            onClick={() => {
              if (confirmation)
                geste.mutate(confirmation, {
                  onSuccess: () => {
                    setConfirmation(null);
                    setAvis(t('evenements.enregistre'));
                  },
                });
            }}
          >
            {t('evenements.confirmer')}
          </Button>
        }
      >
        {geste.isError ? (
          <p role="alert">
            {estApiError(geste.error) ? geste.error.message : t('commun:erreurReseau')}
          </p>
        ) : null}
      </Dialog>
    </section>
  );
}
